import React, {useCallback} from 'react';
import b_ from 'b_';

import {SurveyState} from 'client/common/store/reducers/survey/types';
import {ConstructorTab, Survey} from 'client/common/types';

import {useConstructorContext} from 'client/common/contexts/constructor';

import {usePaymentStatus, usePrice} from 'client/common/hooks/payment';
import {useIsAuthorized, useIsSuperuser} from 'client/common/hooks/user';
import {useSurveyPurpose} from 'client/common/hooks/survey';
import {useCheckSize} from 'client/common/hooks/errors';

import {getIsFree, getSurveyPriceLabels} from 'client/common/utils/price';
import {checkPayed} from 'client/common/utils/payment';
import {genLoginLink} from 'client/common/utils/user-link';

import {Button, Spin} from 'client/common/components/lego';
import {useSettingsMode} from 'client/common/components/survey-settings/hooks';
import {PublicationButton} from 'client/common/components/publication';
import {useAudienceRequest, useAudienceSegments} from 'client/common/hooks/audience';

import I18N from 'assets/i18n/brandlift-project';

import {PublicationButtonProps} from './types';

const b = b_.with('new-constructor');

const i18n = I18N.surveyEditor;

function getPublicationButtonType(state: SurveyState) {
    if (state === SurveyState.Draft || state === SurveyState.Moderation) {
        return 'launch';
    }

    return 'publish';
}

function usePublishSurvey(
    onClick: ((publishCb: () => Promise<void>) => void) | undefined,
    slug: string
) {
    const checkSize = useCheckSize();
    const isSuperUser = useIsSuperuser();
    const request = useAudienceRequest(slug);
    const segments = useAudienceSegments();
    const {setTab} = useConstructorContext();

    return useCallback(
        // eslint-disable-next-line max-statements, complexity
        async (publishCb: () => Promise<void>) => {
            let isSegmentAvailable = false;

            if (request.data?.segments.length && request.data?.type === 'YA_AUDIENCE') {
                const {data: segmentsAvailable} = await segments.refetch();

                if (!segmentsAvailable) return;

                isSegmentAvailable = request.data?.segments.every(selectedSegment =>
                    segmentsAvailable.map(segment => segment.id).includes(Number(selectedSegment))
                );
            }

            const manualOrNone =
                request.data?.type && ['MANUAL', 'NONE'].includes(request.data.type);
            const sizeIsOk = manualOrNone || checkSize();

            const segmentIsValid =
                request.data?.type === 'YA_AUDIENCE' && !isSuperUser ? isSegmentAvailable : true;

            if (sizeIsOk && segmentIsValid) {
                if (onClick) {
                    onClick(publishCb);
                } else {
                    await publishCb();
                }
            } else {
                setTab?.(ConstructorTab.Settings);
            }
        },
        [
            onClick,
            checkSize,
            setTab,
            request.data?.type,
            request.data?.segments,
            segments,
            isSuperUser
        ]
    );
}

function useProps(survey: Survey) {
    const {slug, state} = survey;

    const mode = useSettingsMode(slug);
    const paymentStatus = usePaymentStatus(slug);
    const isSuperuser = useIsSuperuser();
    const purpose = useSurveyPurpose(slug);
    const isAuthorized = useIsAuthorized();

    const isPayed = checkPayed(paymentStatus.data);

    const displayPublishForModeration =
        survey.state === SurveyState.Moderation && (!isPayed || isSuperuser);

    const shouldDisplayPublication =
        ([SurveyState.Active, SurveyState.Paused, SurveyState.Draft].includes(state) ||
            displayPublishForModeration) &&
        isAuthorized;

    return {
        purpose,
        paymentStatus,
        mode,
        shouldDisplayPublication,
        isAuthorized
    };
}

// eslint-disable-next-line max-statements, complexity
function usePriceLabelAttributes(survey: Survey) {
    const {state, slug} = survey;
    const price = usePrice(slug);
    const btnType = getPublicationButtonType(state);
    const paymentStatus = usePaymentStatus(slug);
    const isAuthorized = useIsAuthorized();
    const isPayed = checkPayed(paymentStatus.data);

    let publicationButtonLabel: string = I18N.common.publication.publishChanges;
    let vatHint: string | undefined;

    if (
        (state === SurveyState.Draft || state === SurveyState.Moderation) &&
        price.status === 'success'
    ) {
        const {priceLabel, vatHint: hint} = getSurveyPriceLabels(price.data);
        const isFree = getIsFree(price.data);

        publicationButtonLabel = i18n.publishButtonWithPriceLabel.replace('%s', priceLabel);

        if (isFree || isPayed) {
            publicationButtonLabel = i18n.publishButtonWithLabelFree;
        } else {
            vatHint = hint;
        }
    }
    const displayVatHit =
        (((survey.state === SurveyState.Moderation && !isPayed) ||
            [SurveyState.Active, SurveyState.Paused, SurveyState.Draft].includes(state)) &&
            isAuthorized) ||
        !isPayed;

    const shouldDisplayVatHint = btnType === 'launch' && displayVatHit && vatHint;

    return {price, publicationButtonLabel, vatHint, shouldDisplayVatHint};
}

interface ConstructorPublicationButtonProps
    extends Pick<PublicationButtonProps, 'onModeration' | 'onClick'> {
    survey: Survey;
}

export default function ConstructorPublicationButton(props: ConstructorPublicationButtonProps) {
    const {survey, onClick, onModeration} = props;
    const {state} = survey;

    const {mode, paymentStatus, purpose, shouldDisplayPublication, isAuthorized} = useProps(survey);
    const {price, publicationButtonLabel, vatHint, shouldDisplayVatHint} = usePriceLabelAttributes(
        survey
    );

    const btnType = getPublicationButtonType(state);
    const publish = usePublishSurvey(onClick, survey.slug);

    if (price.status !== 'success' || paymentStatus.status !== 'success') {
        return <Spin className={b('main-spin')} size='s' view='default' progress />;
    }

    return (
        <div>
            {shouldDisplayPublication && (
                <PublicationButton
                    withPreview={mode === 'full'}
                    label={publicationButtonLabel || undefined}
                    isNewView
                    width='max'
                    size='l'
                    className={b('button', {btnType})}
                    onClick={publish}
                    onModeration={onModeration}
                />
            )}
            {!isAuthorized && <LoginButton />}
            {purpose?.selected !== 'yandexSerpSurvey' && shouldDisplayVatHint && (
                <h3 className={b('price-description')}>
                    {i18n.priceWithVatHint.replace('%s', vatHint || '')}
                </h3>
            )}
        </div>
    );
}

function LoginButton() {
    return (
        <Button width='max' size='l' type='link' url={genLoginLink()} view='dl-action'>
            {i18n.loginBtn}
        </Button>
    );
}
